// apps/admin/src/features/roles/PermissionTreeView.tsx
//
// Phase 8.2 Wave 7 Task 51 — Recursive permission tree renderer with
// amount-gate tier selectors for the Admin Roles editor.
//
// Per CONTEXT.md
//   • Catalog is fetched from BE (GET /admin/permissions/catalog)
//                NEVER hardcoded in the FE. Caller is responsible for the
//                fetch; this component consumes a flat list of keys.
//   • Amount-gate keys render with a tier <Select> (up_to_X
//                unlimited) so the operator picks ONE tier per role per
//                action. Selecting `unlimited` automatically enables the
//                parent `up_to_X` key parent-child constraint.
//   • Amount-gated keys (credit_memo:issue, invoice:mark_paid)
//                are SEGREGATION-OF-DUTIES sensitive: `:unlimited` without
//                its `:up_to_X` baseline is rejected by the BE 400. This
//                component co-grants the baseline transparently.
//   • Colon-notation tree. Uses `parsePermissionTree` from
//                lib/permissions.ts to derive the hierarchy at render time.
//
// Tier model (per task spec):
//   - `up_to_100`  — placeholder for the smallest tier (not in current
//                    BE catalog but reserved for future granularity).
//   - `up_to_500`  — kế toán default (credit_memo:issue:up_to_500).
//   - `up_to_2000` — reserved (not in current catalog).
//   - `unlimited`  — admin (issue:unlimited, mark_paid:unlimited).
//   - `up_to_10k`  — invoice mark-paid (kế toán default).
//
//   The component renders ONLY the tiers actually present in the catalog
//   slice for the given parent action (so kế toán's credit_memo:issue
//   shows `up_to_500` + `unlimited`, while invoice:mark_paid shows
//   `up_to_10k` + `unlimited`). Future task-01 catalog extensions just
//   add new keys — no FE change required.
//
// vg-binding: PLAN:task-51
// vg-binding: P8.D-132
// vg-binding: P8.D-135
// vg-binding: P8.D-490
// vg-binding: P8.D-495

import { useMemo } from 'react';
import type { JSX } from 'react';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';
import {
  parseAmountGate,
  parsePermissionTree,
  type PermissionTreeNode,
} from '../../lib/permissions.js';

type Translate = (key: string, vars?: Record<string, unknown>) => string;

// Radix Select forbids empty-string item values. The "no grant" option
// (formerly <option value="">) maps to this sentinel; converted back to the
// original null/'' semantics in the change handler.
const NO_GRANT_VALUE = '__none__';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PermissionTreeViewProps {
  /** All catalog keys (BE-sourced). Order is irrelevant. */
  catalogKeys: ReadonlyArray<string>;
  /** Currently-selected flat colon-keys held by the role. */
  selectedKeys: ReadonlySet<string>;
  /** Optional per-key label map (vi). Defaults to the segment string. */
  labelByKey?: Readonly<Record<string, string>>;
  /** Optional per-key description (shown beneath the row). */
  descriptionByKey?: Readonly<Record<string, string>>;
  /** Read-only mode disables every input. */
  readOnly?: boolean;
  /** Fired when a leaf checkbox toggles. */
  onTogglePermission: (key: string, checked: boolean) => void;
  /**
   * Fired when an amount-gate tier <Select> changes. The component passes
   * the OLD tier keys to revoke + the NEW tier key to grant; the parent
   * MUST apply the diff atomically to avoid the segregation-of-duties
   * violation a partial write would create.
   */
  onChangeAmountTier: (action: {
    /** Parent path joined with `:` (e.g. `credit_memo:issue`). */
    parentPath: string;
    /** Newly-selected tier key (or null when "Không cấp"). */
    nextKey: string | null;
    /** Tier keys to revoke (siblings, excluding `nextKey`). */
    revoke: ReadonlyArray<string>;
  }) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * True iff the node's children are ALL amount-gate variants
 * (`up_to_X` / `unlimited`). When true the tree UI collapses them into a
 * single <Select> tier picker instead of N independent checkboxes.
 */
function isAmountGateAction(node: PermissionTreeNode): boolean {
  if (node.children.length === 0) return false;
  return node.children.every(
    (child) => child.fullKey != null && parseAmountGate(child.fullKey) != null,
  );
}

/**
 * Build the join path for a node (e.g. parent stack
 * `['credit_memo','issue']` → `'credit_memo:issue'`). Used as the
 * stable React key + the audit-friendly parent path emitted by
 * `onChangeAmountTier`.
 */
function buildPath(parentSegments: ReadonlyArray<string>): string {
  return parentSegments.join(':');
}

/**
 * Order amount-gate tier suffixes by ascending numeric cap (unlimited last).
 * Stable ordering keeps the <Select> options predictable across renders.
 */
function sortByCap(
  children: ReadonlyArray<PermissionTreeNode>,
): PermissionTreeNode[] {
  return [...children].sort((a, b) => {
    if (a.fullKey == null || b.fullKey == null) return 0;
    const gateA = parseAmountGate(a.fullKey);
    const gateB = parseAmountGate(b.fullKey);
    if (gateA == null || gateB == null) return 0;
    if (gateA.unlimited && !gateB.unlimited) return 1;
    if (gateB.unlimited && !gateA.unlimited) return -1;
    return (gateA.cap ?? 0) - (gateB.cap ?? 0);
  });
}

/**
 * Format a numeric cap as a Vietnamese-friendly currency hint. The catalog
 * already carries the canonical label but for fallback we
 * stringify the cap so the <Select> option remains legible.
 */
function formatTierLabel(
  node: PermissionTreeNode,
  parentPath: string,
  t: Translate,
): string {
  if (node.fullKey == null) return node.segment;
  const gate = parseAmountGate(node.fullKey);
  if (gate == null) return node.segment;
  if (gate.unlimited) return t('permission.tree.tierUnlimited');
  // Heuristic prefix for currency hint — invoice/mark_paid is USD-cap
  // (`up_to_10k` = 10,000 USD); credit_memo:issue is also USD
  // (`up_to_500` = 500 USD). All Phase 8.2 amount-gate keys are USD per
  // the catalog. Future VND-prefixed keys would need a per-module
  // resolver — out of scope for task 51.
  const cap = gate.cap ?? 0;
  const formatted = cap.toLocaleString(undefined);
  void parentPath; // reserved for future per-module currency override
  return t('permission.tree.tierUpTo', { amount: formatted });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PermissionTreeView({
  catalogKeys,
  selectedKeys,
  labelByKey,
  descriptionByKey,
  readOnly = false,
  onTogglePermission,
  onChangeAmountTier,
}: PermissionTreeViewProps): JSX.Element {
  const { t } = useTranslation('users');
  const tree = useMemo(
    () => parsePermissionTree(catalogKeys),
    [catalogKeys],
  );

  if (tree.length === 0) {
    return (
      <div
        className="rounded-md border border-pw-border bg-pw-surface p-3 text-sm text-pw-text-muted"
        data-testid="admin-roles-perm-tree-empty"
      >
        {t('permission.tree.empty')}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3" data-testid="admin-roles-perm-tree">
      {tree.map((root) => (
        <TreeNodeView
          key={root.segment}
          node={root}
          parentSegments={[]}
          depth={0}
          selectedKeys={selectedKeys}
          labelByKey={labelByKey}
          descriptionByKey={descriptionByKey}
          readOnly={readOnly}
          onTogglePermission={onTogglePermission}
          onChangeAmountTier={onChangeAmountTier}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Internal recursive renderer
// ---------------------------------------------------------------------------

interface TreeNodeViewProps {
  node: PermissionTreeNode;
  parentSegments: ReadonlyArray<string>;
  depth: number;
  selectedKeys: ReadonlySet<string>;
  labelByKey?: Readonly<Record<string, string>>;
  descriptionByKey?: Readonly<Record<string, string>>;
  readOnly: boolean;
  onTogglePermission: (key: string, checked: boolean) => void;
  onChangeAmountTier: PermissionTreeViewProps['onChangeAmountTier'];
}

function TreeNodeView({
  node,
  parentSegments,
  depth,
  selectedKeys,
  labelByKey,
  descriptionByKey,
  readOnly,
  onTogglePermission,
  onChangeAmountTier,
}: TreeNodeViewProps): JSX.Element {
  const { t } = useTranslation('users');
  const path = buildPath([...parentSegments, node.segment]);

  // ── Case 1: amount-gate action (collapse children into a <Select>) ────
  if (isAmountGateAction(node)) {
    const tiers = sortByCap(node.children);
    const currentTier =
      tiers.find((t) => t.fullKey != null && selectedKeys.has(t.fullKey)) ??
      null;
    const value = currentTier?.fullKey ?? '';
    const tierIds = tiers
      .map((t) => t.fullKey)
      .filter((k): k is string => typeof k === 'string');

    return (
      <div
        className="rounded-md border border-pw-border bg-pw-bg"
        data-amount-gate-action={path}
        style={{ marginLeft: depth * 16 }}
      >
        <div className="flex items-center justify-between gap-3 px-3 py-2">
          <div className="flex flex-col">
            <span className="text-sm font-medium text-pw-text">
              {labelByKey?.[path] ?? node.segment}
              <span className="ml-2 rounded-full bg-amber-50 dark:bg-amber-950/40 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-800 dark:text-amber-300">
                {t('permission.tree.amountGateBadge')}
              </span>
            </span>
            <span className="text-xs text-pw-text-muted">
              {t('permission.tree.amountGateHint')}
            </span>
          </div>
          <div className="min-w-[220px]">
            <Select
              disabled={readOnly}
              value={value === '' ? NO_GRANT_VALUE : value}
              onValueChange={(v) => {
                const next = v === NO_GRANT_VALUE ? null : v;
                const revoke = tierIds.filter((k) => k !== next);
                onChangeAmountTier({
                  parentPath: path,
                  nextKey: next,
                  revoke,
                });
              }}
            >
              <SelectTrigger
                data-testid={`admin-roles-amount-gate-select-${path}`}
                aria-label={t('permission.tree.thresholdAria', { path })}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_GRANT_VALUE}>
                  {t('permission.tree.noGrant')}
                </SelectItem>
                {tiers.map((tier) =>
                  tier.fullKey ? (
                    <SelectItem key={tier.fullKey} value={String(tier.fullKey)}>
                      {formatTierLabel(tier, path, t)}
                    </SelectItem>
                  ) : null,
                )}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>
    );
  }

  // ── Case 2: terminal leaf (single checkbox) ────────────────────────────
  if (node.fullKey != null && node.children.length === 0) {
    const key = node.fullKey;
    const checked = selectedKeys.has(key);
    return (
      <label
        className="flex cursor-pointer items-start gap-2 px-3 py-1.5 text-sm"
        style={{ marginLeft: depth * 16 }}
        data-perm-leaf={key}
      >
        <input
          type="checkbox"
          data-testid={`admin-roles-perm-checkbox-${key}`}
          checked={checked}
          disabled={readOnly}
          onChange={(e) => onTogglePermission(key, e.target.checked)}
          className="mt-0.5 h-4 w-4 accent-pw-primary"
        />
        <span className="flex flex-col">
          <span className="font-medium text-pw-text">
            {labelByKey?.[key] ?? node.segment}
          </span>
          {descriptionByKey?.[key] ? (
            <span className="text-xs text-pw-text-muted">
              {descriptionByKey[key]}
            </span>
          ) : (
            <span className="font-mono text-[11px] text-pw-text-muted">
              {key}
            </span>
          )}
        </span>
      </label>
    );
  }

  // ── Case 3: branch (recurse into children) ─────────────────────────────
  return (
    <div
      className="flex flex-col gap-1"
      data-perm-branch={path}
      style={{ marginLeft: depth * 16 }}
    >
      <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-pw-text-muted">
        {labelByKey?.[path] ?? node.segment}
      </div>
      <div className="flex flex-col gap-1">
        {node.children.map((child) => (
          <TreeNodeView
            key={child.segment}
            node={child}
            parentSegments={[...parentSegments, node.segment]}
            depth={depth + 1}
            selectedKeys={selectedKeys}
            labelByKey={labelByKey}
            descriptionByKey={descriptionByKey}
            readOnly={readOnly}
            onTogglePermission={onTogglePermission}
            onChangeAmountTier={onChangeAmountTier}
          />
        ))}
      </div>
    </div>
  );
}

export default PermissionTreeView;
